<script lang="ts">
  import { SvelteComponent, createEventDispatcher } from 'svelte';
  import { uuidv4, clamp } from '$lib/utils';
  import { focusAction } from '$lib/directives/focusAction';

  import Select from 'svelte-select';
  import ItemComponent from 'svelte-select/src/Item.svelte';
  import SelectionComponent from 'svelte-select/src/Selection.svelte';
  import MultiSelectionComponent from 'svelte-select/src/MultiSelection.svelte';
  import FontAwesomeIcon from '$components/FontAwesomeIcon.svelte';
  import InfoIcon from '$mds/InfoIcon';
  import Image from '$mds/Image';
  import { faXmark, faEye, faEyeSlash, faExclamation, type IconDefinition } from '@fortawesome/pro-solid-svg-icons';

  const dispatch = createEventDispatcher();

  // Base options
  export let id = `a${uuidv4()}`;
  export let label: string | null = null;
  export let labelSecondary: string | null = null;
  export let labelHelp: string | null = null;
  export let labelClass = 'text-body-2xs';
  $: hasLabel = !!label || $$slots.label || labelSecondary;
  export let inputContainerClass = '';
  export let type:
    | 'text'
    | 'search'
    | 'password'
    | 'email'
    | 'number'
    | 'date'
    | 'time'
    | 'select'
    | 'textarea'
    | 'checkbox'
    | 'radio'
    | 'toggle'
    | 'url'
    | 'custom' = 'text';
  export let placeholder = '';
  export let help: string | null = null;
  export let helpTooltip: string | null = null;
  export let disabled = false;
  export let required = false;
  // Specifies if to show validation error messages inline. If false, no error is shown.
  export let showValidationMessage = true;
  export let action: string | null = null;
  export let actionLink = false;
  export let actionCustom = false;
  export let value: any = null;
  export let checked: boolean | null = null;
  export let group: string | string[] | null = null;
  export let name = '';
  export let spellcheck: boolean | null = null;
  export let isFocused = false;
  export let isClearable = false;
  export let tabindex: number | undefined = undefined;
  let className = '';
  export { className as class };
  export let el: HTMLInputElement | HTMLTextAreaElement | HTMLElement | null = null;
  export function focus(moveCursorToEnd = false) {
    if (!el) return;
    el.focus();
    if (moveCursorToEnd) {
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }
  export function select() {
    if (!el) return;
    el.focus();
    el.setSelectionRange(0, el.value.length);
  }

  export let pattern: string | undefined = undefined;
  export let customValidationMessage: string | null = null;
  // Exported for outside visibility
  export let validationMessage = '';
  // Only start showing validation error after user has interacted with the input.
  let userInteracted = false;
  function updateValidation(event = null) {
    userInteracted = true;
    if (!event || event.target?.validationMessage === '') {
      validationMessage = '';
      return;
    }
    validationMessage = customValidationMessage || event.target.validationMessage;
  }

  $: checkEntries =
    typeof spellcheck !== 'boolean'
      ? {}
      : spellcheck
        ? { spellcheck: 'true', autocorrect: 'on' }
        : { spellcheck: 'false', autocorrect: 'off' };

  // Input options
  export let maxlength: number | undefined = undefined;
  export let max: number | undefined = undefined;
  export let min: number | string | undefined = undefined;
  export let step: number | undefined = undefined;
  export let showNumberSpinner = true;
  export let readonly = false;
  export let inlineText = '';
  export let inlineTextStyle: 'fixed' | 'inline' = 'fixed';
  export let inlineTextSize = 'text-body-base';
  export let inlineTextDirection: 'right' | 'left' = 'right';
  export let inlinePadding: number | string | null = null;
  export let inlinePaddingDirection = 'right';
  export let isCurrencyInput = false;
  export let currencySymbol = '$';
  export let currencyInputClass = 'absolute top-0 bottom-0 flex items-center left-3 text-body-base';

  // Select options
  type SelectedItem = { label?: string; value?: any; [key: string]: any } | string | number;
  export let inputEl: HTMLInputElement | null = null;
  export let items: SelectedItem[] = [];
  export let selectedValue: SelectedItem | SelectedItem[] | null = null;
  export let isMulti = false;
  export let placeholderAlwaysShow = false;
  export let multiFullItemClearable = false;
  export let isCreatable = false;
  export let isSearchable = true;
  export let isWaiting = false;
  export let noOptionsMessage: string | undefined = 'No items found.';
  export let maxOptionsMessage: string | undefined = `Max ${max} items`;
  export let listPlacement = 'auto';
  export let listAutoWidth = true;
  export let showIndicator = !isMulti;
  export let hideEmptyState = false;
  /** component to render the items in the list */
  export let Item: SvelteComponent = ItemComponent;
  /** component to render the selected items in the text field (single select) */
  export let Selection: SvelteComponent = SelectionComponent;
  /** component to render the selected items in the text field (multi select) */
  export let MultiSelection: SvelteComponent = MultiSelectionComponent;
  export let getOptionLabel = (option: any, filterText: string) => option['label'];
  export let getSelectionLabel = (option: any) => option['label'];
  export let loadOptions: ((filterText: string) => Promise<any[]>) | undefined = undefined;
  export let createItem: ((inputValue: string) => any) | undefined = undefined;
  export let selectImage: string | null = null;

  $: isAtMax = isMulti && max && selectedValue && selectedValue.length >= max;
  $: _noOptionsMessage = isAtMax ? maxOptionsMessage : noOptionsMessage;
  $: _items = isAtMax ? [] : items;
  $: _isCreateable = !isAtMax && isCreatable;
  $: _placeholderAlwaysShow = !isAtMax && placeholderAlwaysShow;

  // Icon options
  export let leftIcon: IconDefinition | null = null;
  export let rightIcon: IconDefinition | null = null;
  export let iconProps = {};
  // Textarea options
  export let autoSize = false;
  export let autoSizeMax: number | null = null;
  export let rows = 3;
  /** Provide a string to display a custom counter label, or true to use the default 'characters left' label */
  export let counter: boolean | string = false;
  export let counterLimit = maxlength;
  let counterLimitNear = counterLimit ? counterLimit - clamp(counterLimit * 0.1, 26, 51) : 0;
  $: hasCounter = (counter || typeof counter === 'string') && !!counterLimit;

  // Toggle options
  export let toggleLabelClass = '';
  export let isBigToggle = false;

  // Checkbox options
  export let altColors = false;

  // Flags
  let showPassword = false;

  // Info icon
  let labelEl: HTMLLabelElement;
  let helpIconEl: HTMLElement;
</script>

{#if hasLabel || action || labelHelp}
  <div class="relative mb-2 flex flex-row items-end justify-between">
    <div class="group flex items-center space-x-2">
      {#if hasLabel}
        <label
          bind:this={labelEl}
          for={id}
          class="formcontrol-label flex gap-2 {labelClass} {disabled ? 'text-ondark-subtle' : 'text-ondark-secondary'}"
          on:click={() => dispatch('labelClick')}
        >
          <slot name="label">
            {label}
            {#if labelSecondary}
              <span class="text-ondark-subtle">{labelSecondary}</span>
            {/if}
          </slot>
        </label>
      {/if}
      {#if helpTooltip}
        <InfoIcon
          bind:node={helpIconEl}
          content={helpTooltip}
          placement="top"
          hideOnClick={true}
          target={[labelEl, helpIconEl]}
        />
      {/if}
    </div>
    {#if labelHelp || hasCounter}
      <div class="text-ondark-subtle text-body-4xs self-end justify-self-end">
        <slot name="labelHelp">
          {#if hasCounter && !!counterLimit}
            <span
              class="textcounter ease-expo-out pointer-events-none transition-colors duration-200"
              class:disabled
              class:is-near-limit={(value ?? '').length > counterLimitNear && (value ?? '').length < counterLimit}
              class:is-over-limit={(value ?? '').length >= counterLimit - 5}
            >
              <span class="inline-block">
                {counterLimit - (value ?? '').length}
                {typeof counter === 'string' ? counter : 'characters left'}
              </span>
            </span>
          {/if}
          {#if labelHelp}
            <span>
              {labelHelp}
            </span>
          {/if}
        </slot>
      </div>
    {/if}
    {#if action}
      {#if actionLink}
        <a
          href={actionLink}
          tabindex={-1}
          class="text-functional-r00 hover:text-functional-r20 block text-xs font-semibold leading-4 transition-colors duration-200 focus:outline-none"
        >
          {action}
        </a>
      {:else}
        <button
          type="button"
          class="text-functional-r00 hover:text-functional-r20 block text-xs font-semibold leading-4
            transition-colors duration-200 focus:outline-none"
          on:click={() => dispatch('action')}
        >
          {action}
        </button>
      {/if}
    {:else if actionCustom}
      <slot name="action" />
    {/if}
  </div>
{/if}
{#if type === 'select'}
  <span class="select text-onlight-secondary relative block {className}" class:invalid={validationMessage !== ''}>
    {#if selectImage}
      <Image
        src={selectImage}
        alt="Select"
        class="z-1 pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2"
        sizes={{ xs: 40 }}
        aspect={1}
        rounded="rounded-full"
        fit="cover"
      />
    {/if}
    <Select
      bind:input={inputEl}
      bind:container={el}
      bind:value={selectedValue}
      bind:isWaiting
      bind:isFocused
      items={_items}
      {placeholder}
      {isClearable}
      isCreatable={_isCreateable}
      {isSearchable}
      {isMulti}
      {multiFullItemClearable}
      placeholderAlwaysShow={_placeholderAlwaysShow}
      noOptionsMessage={_noOptionsMessage}
      {listPlacement}
      {listAutoWidth}
      {showIndicator}
      {hideEmptyState}
      {Selection}
      {Item}
      {MultiSelection}
      {getOptionLabel}
      {getSelectionLabel}
      {loadOptions}
      {createItem}
      itemFilter={(label, filterText, option) => {
        return `${label}`.toLowerCase().includes(filterText.toLowerCase());
      }}
      isDisabled={disabled}
      inputAttributes={{ id, ...checkEntries, tabindex, disabled, class: isAtMax ? 'hidden' : '', maxlength }}
      on:select={event => {
        // Clear validation errors when a value is selected
        updateValidation(null);
        // Forward event
        dispatch('select', event.detail);
      }}
      on:clear
    />
    {#if showValidationMessage && required}
      <!-- Show a dummy <select> element we can use to trigger validation. -->
      <select hidden class="hidden" {required} on:invalid={updateValidation}>
        {#if isMulti ? Array.isArray(selectedValue) && selectedValue.length > 0 : selectedValue}
          <option selected>Option</option>
        {/if}
      </select>
    {/if}
  </span>
{:else if type === 'textarea'}
  <div
    id="{id}-wrapper"
    class="textarea-wrapper {autoSize
      ? 'grid *:col-start-1 *:row-start-1 after:invisible after:col-start-1 after:row-start-1 after:whitespace-pre-wrap after:content-[attr(data-replicated-value)_"_"]'
      : ''}"
    style={autoSizeMax ? `max-height: ${autoSizeMax}px;` : ''}
    data-replicated-value={value}
  >
    <textarea
      bind:this={el}
      {id}
      {placeholder}
      {...checkEntries}
      {disabled}
      {required}
      {maxlength}
      {tabindex}
      bind:value
      {rows}
      class="textarea block {className}"
      class:autoSize
      class:autoSizeScrolling={autoSizeMax && autoSizeMax <= el?.scrollHeight}
      class:over-limit={maxlength && value?.length > maxlength}
      style={autoSizeMax ? `max-height: ${autoSizeMax}px;` : ''}
      class:userInteracted
      on:input={updateValidation}
      on:invalid={updateValidation}
      on:input
      on:keyup
      on:keydown
      on:click
      on:paste
    ></textarea>
  </div>
{:else if type === 'checkbox'}
  <input
    bind:this={el}
    {id}
    {name}
    type="checkbox"
    {placeholder}
    {disabled}
    {required}
    {tabindex}
    bind:checked
    class="h-6 w-6 {!disabled
      ? 'hover:bg-ondark-low cursor-pointer'
      : ''} transition-colors duration-200 focus:ring-0 focus:ring-offset-0 {className}"
    class:altColors
    on:change={e =>
      e?.target?.checked ? (group = [...(group || []), value]) : (group = group?.filter(v => v !== value))}
    on:change
  />
{:else if type === 'radio'}
  <input
    bind:this={el}
    {id}
    {name}
    type="radio"
    {placeholder}
    {disabled}
    {required}
    {checked}
    {tabindex}
    class="h-5 w-5 {!disabled
      ? 'cursor-pointer'
      : ''} transition-colors duration-200 focus:ring-0 focus:ring-offset-0 {className}"
    on:change={() => (group = value)}
    on:change
  />
{:else if type === 'toggle'}
  <label for={id} class="toggle-label {toggleLabelClass}" class:isBigToggle>
    <input
      bind:this={el}
      {id}
      {name}
      type="checkbox"
      {placeholder}
      {disabled}
      {required}
      {tabindex}
      bind:checked
      class="form-toggle cursor-pointer {className}"
      on:change
    />
    <div class="slider" />
  </label>
{:else if type === 'custom'}
  <slot />
{:else}
  <div
    class="{inputContainerClass} group"
    class:relative={!!inlineText ||
      !!isCurrencyInput ||
      type === 'password' ||
      isClearable ||
      !!leftIcon ||
      !!rightIcon}
    style={inlinePadding !== null ? `--inline-padding: ${inlinePadding}px;` : ''}
  >
    {#if leftIcon}
      <span class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
        <FontAwesomeIcon icon={leftIcon} class="text-ondark-secondary size-4" {...iconProps} />
      </span>
    {/if}
    {#if isCurrencyInput}
      <span
        class="currency-symbol pointer-events-none transition-colors duration-200 ease-in-out
          {disabled
          ? 'text-ondark-subtle'
          : 'text-ondark-secondary group-focus-within:text-ondark-subtle'} {currencyInputClass}"
      >
        {currencySymbol}
      </span>
    {/if}
    {#if rightIcon}
      <span class="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
        <FontAwesomeIcon icon={rightIcon} class="text-ondark-subtle size-4" {...iconProps} />
      </span>
    {/if}
    <!-- Compiler won't allow dynamic types... hackish bypass -->
    <input
      {...{ type: showPassword ? 'text' : type }}
      bind:this={el}
      {id}
      {placeholder}
      {...checkEntries}
      {disabled}
      {required}
      {maxlength}
      {min}
      {max}
      {step}
      {pattern}
      {readonly}
      {tabindex}
      bind:value
      class="input {className}"
      class:hide-number-spinner={!showNumberSpinner}
      class:inlinePadding={inlinePadding !== null}
      class:inlinePaddingLeft={inlinePaddingDirection === 'left'}
      class:inlinePaddingRight={inlinePaddingDirection === 'right'}
      class:inlinePaddingBoth={inlinePaddingDirection === 'both'}
      class:isCurrencyInput
      class:pl-[44px]={leftIcon}
      class:pr-[44px]={isClearable}
      class:userInteracted
      on:input={updateValidation}
      on:invalid={updateValidation}
      on:input
      on:change
      on:click
      on:keyup
      on:keydown
      on:blur
      on:paste
      on:focus
      use:focusAction={isFocused}
    />
    {#if isClearable && type !== 'password'}
      <button
        type="button"
        tabindex="-1"
        class="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 opacity-50 transition-opacity hover:opacity-100"
        on:click={() => {
          value = '';
          dispatch('clear');
        }}
      >
        <FontAwesomeIcon icon={faXmark} class="m-auto h-3.5" />
      </button>
    {/if}
    {#if type === 'password'}
      <button
        type="button"
        class="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2"
        on:click|preventDefault={() => (showPassword = !showPassword)}
      >
        <FontAwesomeIcon
          icon={showPassword ? faEye : faEyeSlash}
          class="m-auto h-3.5 {showPassword ? 'text-ondark-primary' : 'text-ondark-secondary'}"
        />
      </button>
    {/if}
    {#if inlineText && inlineTextStyle === 'fixed'}
      <span
        class="inline-text text-ondark-secondary pointer-events-none absolute bottom-0 top-0 flex items-center {inlineTextSize}"
        class:right-4={inlineTextDirection === 'right'}
        class:left-4={inlineTextDirection === 'left'}
      >
        {inlineText}
      </span>
    {/if}
    {#if inlineText && inlineTextStyle === 'inline'}
      <div
        class="{inlinePadding !== null
          ? 'p-0'
          : 'pl-4'} pointer-events-none absolute inset-0 flex items-center gap-2 pr-1"
      >
        <span
          class="input !w-max max-w-full shrink-0 truncate p-0 opacity-0 {inlinePadding !== null
            ? 'inlinePadding'
            : ''}"
          class:inlinePaddingLeft={inlinePaddingDirection === 'left'}
          class:inlinePaddingRight={inlinePaddingDirection === 'right'}
          class:inlinePaddingBoth={inlinePaddingDirection === 'both'}>{value}</span
        >
        <span class="text-ondark-secondary truncate {inlineTextSize}">{inlineText}</span>
      </div>
    {/if}
  </div>
{/if}
{#if showValidationMessage && validationMessage}
  <label for={id} class="text-alert-ondark-base ml-1 mt-2 flex items-center">
    <FontAwesomeIcon icon={faExclamation} class="validation-icon h-3.5" />
    <p class="validation-text text-body-2xs sm:text-body-4xs ml-2">{validationMessage}</p>
  </label>
{/if}
{#if help}
  <span class="text-ondark-subtle mt-2 block text-xs leading-4 md:mt-3">{@html help}</span>
{/if}

<style lang="postcss">
  /* Note: The input and textarea styles are located in "src/assets/styles/global.css" */

  /* Custom select */
  /* https://github.com/rob-balfre/svelte-select/blob/master/docs/theming_variables.md */
  .select {
    --background: theme('backgroundColor.ondark.medium');
    --border: 1px solid theme('borderColor.ondark.subtle');
    --borderFocusColor: transparent;
    --borderHoverColor: theme('backgroundColor.ondark.high');
    --borderRadius: theme('borderRadius.lg');
    --clearSelectColor: theme('colors.nl.2');
    --clearSelectFocusColor: theme('colors.nl.2');
    --clearSelectHoverColor: theme('colors.nl.2');
    --clearSelectWidth: 16px;
    --disabledBackground: theme('backgroundColor.action.ondark.inactive');
    --disabledBorderColor: theme('backgroundColor.transparent');
    --disabledColor: theme('textColor.ondark.subtle');
    --disabledPlaceholderColor: theme('colors.nl.2 / 60%');
    --errorBorder: 1px solid theme('borderColor.t.8');
    --indicatorColor: theme('textColor.ondark.secondary');
    --indicatorFill: theme('textColor.ondark.secondary');
    --indicatorRight: theme('spacing.3');
    --indicatorTop: 50%;
    --inputColor: theme('colors.nl.0');
    --inputFontSize: theme('fontSize.base');
    --inputLetterSpacing: theme('letterSpacing.normal');
    --itemActiveBackground: theme('backgroundColor.nd.5');
    --itemFirstBorderRadius: var(--borderRadius);
    --itemHoverBG: theme('backgroundColor.ondark.medium');
    --itemIsActiveBG: theme('backgroundColor.nd.5');
    --itemIsActiveColor: theme('colors.a.5');
    --itemColor: theme('colors.nl.0');
    --itemPadding: 0.5rem;
    --listBorder: 1px solid theme('borderColor.ondark.subtle');
    --listBackground: theme('backgroundColor.ondark.low');
    --listBorderRadius: theme('borderRadius.2xl');
    --listEmptyColor: theme('colors.neutrals.l20');
    --listShadow: theme('boxShadow.md');
    --multiClearBG: theme('backgroundColor.nd.8');
    --multiClearFill: theme('colors.nl.2');
    --multiClearHoverBG: theme('backgroundColor.nd.8');
    --multiClearHoverFill: theme('colors.nl.2');
    --multiClearRadius: theme('borderRadius.md');
    --multiClearTop: 9px;
    --multiItemActiveBG: theme('backgroundColor.nd.8');
    --multiItemActiveColor: theme('colors.nl.0');
    --multiItemDisabledHoverBg: theme('backgroundColor.nd.8');
    --multiItemDisabledHoverColor: theme('colors.nl.0');
    --multiItemBG: theme('backgroundColor.ondark.low');
    --multiItemBorderRadius: theme('borderRadius.md');
    --multiItemHeight: 32px;
    --multiItemMargin: 0;
    --multiLabelMargin: 0 5px 0 0;
    --multiSelectPadding: 0.5rem;
    --multiSelectInputPadding: 0 0 0 0.5rem;
    --placeholderColor: theme('backgroundColor.nl.2');
    /* --listPosition: fixed; */
    @apply text-ondark-primary;
    @screen md {
      --inputFontSize: theme('fontSize.base');
      --multiItemMargin: 0;
    }
    &:hover,
    &:has(input:hover) {
      --background: theme('backgroundColor.ondark.high');
    }
  }

  :global(.select),
  :global(.select ~ .custom-spinner) {
    --height: 44.5px;
    &.selectContainer.multiSelect {
      --height: 32px;
    }
    @screen sm {
      --height: 47px;
    }
  }

  :global(.select .listItem) {
    --height: auto;
  }
  :global(.select .listItem > .item) {
    @apply rounded-lg;
  }

  :global(.selectContainer.selectContainer.selectContainer) {
    @apply text-body-base;
    @apply ease-expo-in transition-[color,background-color,border-color,text-decoration-color,fill,stroke,box-shadow] delay-0 duration-300;
  }
  :global(.selectContainer input) {
    box-shadow: none !important; /* has to be normal css, tailwind doesn't work */
    @apply text-base;
    &::placeholder {
      @apply !text-ondark-subtle text-sm opacity-100 md:text-base;
    }
    &:not([readonly]) {
      @apply !cursor-text;
    }
  }
  :global(.select > .selectContainer.focused) {
    @apply ring-ondark-focused ring-2 ring-offset-0;
    @apply ease-smooth-out duration-300;
  }
  :global(.select .listContainer:empty) {
    @apply hidden;
  }
  :global(.selectContainer.multiSelect) {
    @apply gap-1;
    --height: 32px;
  }
  :global(.selectContainer.selectContainer.selectContainer:not(.multiSelect) > .spinner),
  :global(.select ~ .custom-spinner) {
    top: calc((var(--height) / 2));
    translate: 0 -50%;
  }
  :global(.select ~ .custom-spinner--label) {
    top: calc((var(--height) / 2) + 30px);
  }
  :global(.selectContainer.multiSelect > input) {
    @apply min-w-32;
  }
  :global(.selectContainer .multiSelectItem) {
    @apply text-ondark-primary text-sm;
  }
  :global(.selectContainer .selectedItem) {
    @apply flex items-center overflow-y-hidden text-base;
  }
  :global(.listContainer) {
    @apply bg-ondark-low border-ondark-subtle shadow-newLg rounded-2xl border p-2;
  }
  :global(.select .indicator.indicator) {
    @apply animate-fade-in-slow flex -translate-y-1/2;
    & :global(svg) {
      @apply block transition-colors;
    }
  }
  :global(.select:hover .selectContainer .indicator.indicator) {
    --indicatorColor: theme('textColor.ondark.primary');
    --indicatorFill: theme('textColor.ondark.primary');
  }

  /* Custom toggle */
  .toggle-label {
    @apply relative inline-block shrink-0;
    width: 34px;
    height: 16px;

    & input.form-toggle {
      @apply absolute h-0 w-0 text-[0] !opacity-0;

      &:checked {
        & + .slider {
          @apply bg-positive-ondark-base;
        }
        & + .slider:before {
          @apply bg-ondark-medium;
          transform: translate(calc(34px - 12px - 2px), 2px);
        }
      }
      &:disabled {
        & + .slider {
          @apply bg-action-ondark-inactive;
        }
        & + .slider:before {
          @apply bg-neutral-400;
        }
      }
      &:checked:disabled {
        & + .slider {
          @apply bg-positive-ondark-base/30 saturate-50;
        }
        & + .slider:before {
          @apply bg-neutral-400;
        }
      }

      &:focus-visible + .slider {
        @apply ring-ondark-focused ring-2 ring-offset-0;
      }
    }

    & .slider {
      @apply bg-ondark-high absolute -left-px -top-px h-full w-full cursor-pointer;
      @apply box-content rounded-full;
      @apply transition-all duration-300 ease-in-out;
      @apply ring-2 ring-transparent;

      &:before {
        @apply bg-onlight-base absolute rounded-full;
        @apply transition-all duration-300 ease-in-out;
        content: '';
        height: 12px;
        width: 12px;
        transform: translate(2px, 2px);
      }
    }

    &.isBigToggle {
      width: 40px;
      height: 24px;

      & .slider {
        @apply -left-px -top-px rounded-2xl;
      }
      & input.form-toggle:checked + .slider {
        &:before {
          transform: translate(calc(40px - 20px - 2px), 2px);
        }
        &:after {
          opacity: 1;
          transform: translate(calc(40px - 16px - 4px), 4px);
        }
      }
      & input.form-toggle:checked:disabled + .slider:after {
        opacity: 0;
      }
      & .slider {
        &:before {
          height: 20px;
          width: 20px;
          transform: translate(2px, 2px);
        }

        &:after {
          @apply absolute h-4 w-4 rounded-full opacity-0;
          @apply transition-all duration-300 ease-in-out;
          content: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 17 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.70001 8.36152L7.23333 10.7L12.3 5' stroke='rgb%28148 240 148%29' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
          transform: translate(4px, 4px);
        }
      }
    }
  }
  .input.inlinePadding.inlinePaddingRight {
    padding-right: var(--inline-padding);
  }
  .input.inlinePadding.inlinePaddingLeft {
    padding-left: var(--inline-padding);
  }
  .input.inlinePadding.inlinePaddingBoth {
    padding-left: var(--inline-padding);
    padding-right: var(--inline-padding);
  }

  input:user-invalid,
  textarea:user-invalid,
  textarea.over-limit {
    border-color: theme('colors.alert.ondark.base');
  }
  .is-near-limit {
    @apply text-action-ondark-base transition-colors;
  }
  .is-over-limit {
    @apply text-alert-ondark-base transition-colors;
  }
  .textcounter.disabled {
    color: theme('colors.neutral.700');
  }
  .select.invalid {
    --border: 1px solid theme('colors.alert.ondark.base');
    --borderFocusColor: theme('colors.alert.ondark.base');
    --borderHoverColor: theme('colors.alert.ondark.base');
  }

  input[type='checkbox']:not(:checked):disabled:hover {
    @apply border-ondark-base bg-transparent;
  }

  input.isCurrencyInput {
    --defaultPadding: theme('spacing.3');
    --currencyPadding: 13px;
    padding-left: calc(var(--defaultPadding) + var(--currencyPadding));
    @screen md {
      --defaultPadding: theme('spacing.4');
      --currencyPadding: 9px;
    }
  }

  .autoSize {
    overflow: hidden;
    resize: none;
  }
  .autoSize.autoSizeScrolling {
    overflow: auto;
  }

  input[type='date'] {
    color-scheme: dark;
  }

  input[type='date']::-webkit-calendar-picker-indicator,
  input[type='time']::-webkit-calendar-picker-indicator {
    filter: invert(57%) sepia(9%) saturate(660%) hue-rotate(205deg) brightness(93%) contrast(88%);
    font-size: 16px;
    margin: 0;
  }

  input[type='time'] {
    max-height: 46px;
  }

  input[type='text'].youtube-input {
    @apply bg-ondark-low;

    &:hover {
      @apply bg-ondark-medium;
    }

    &:focus {
      @apply bg-ondark-low;
      @apply border-ondark-subtle;
    }

    &:disabled {
      @apply border-ondark-subtle;
    }
  }

  input[type='text'].title-input {
    @apply bg-ondark-low;

    &:hover {
      @apply bg-ondark-medium;
    }

    &:focus {
      @apply bg-ondark-low;
      @apply border-ondark-subtle;
    }

    &:disabled {
      @apply border-ondark-subtle;
      --placeholderColor: theme('textColor.nl.5');
    }
  }

  .game-select {
    --placeholderColor: theme('colors.nl.0');

    &:disabled {
      --placeholderColor: theme('colors.nl.5');
    }

    --background: theme('backgroundColor.nd.8');
    &:hover {
      --background: theme('backgroundColor.nd.5');
    }
  }

  .game-select Select .selectContainer {
    /* --background: theme('backgroundColor.nd.8'); */
    --borderFocusColor: theme('borderColor.nd.5');

    @apply bg-ondark-low;

    &:focus {
      @apply bg-ondark-low;
      --background: theme('backgroundColor.nd.9');
      --border: theme('borderColor.nd.5');
    }

    &:disabled {
      --border: theme('borderColor.nl.5');
    }
  }

  input[type='checkbox'].altColors {
    &:checked {
      @apply bg-q-5;
    }
    &:checked:hover {
      @apply bg-q-2/80;
    }
    &:checked:focus {
      @apply !bg-q-5;
    }
  }

  input[type='number'] {
    &::-webkit-inner-spin-button,
    &::-webkit-inner-spin-button {
      @apply appearance-none opacity-0;
    }
    &:not(.hide-number-spinner) {
      &::-webkit-inner-spin-button {
        @apply bg-ondark-medium w-7.5 h-10 rounded-lg transition-all;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='30' height='40' viewBox='0 0 24 24' stroke-width='1' stroke='white' fill='none' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M8 8l4 -4l4 4' /%3E%3Cpath d='M16 16l-4 4l-4 -4' /%3E%3C/svg%3E");
      }
      &:hover:not(:disabled),
      &:focus-visible:not(:disabled) {
        &::-webkit-inner-spin-button {
          @apply opacity-80;
        }
      }
    }
  }

  input[type='search']::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
    height: 10px;
    width: 10px;
    background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pg0KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDE2LjAuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPg0KPCFET0NUWVBFIHN2ZyBQVUJMSUMgIi0vL1czQy8vRFREIFNWRyAxLjEvL0VOIiAiaHR0cDovL3d3dy53My5vcmcvR3JhcGhpY3MvU1ZHLzEuMS9EVEQvc3ZnMTEuZHRkIj4NCjxzdmcgdmVyc2lvbj0iMS4xIiBpZD0iQ2FwYV8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCINCgkgd2lkdGg9IjEyMy4wNXB4IiBoZWlnaHQ9IjEyMy4wNXB4IiB2aWV3Qm94PSIwIDAgMTIzLjA1IDEyMy4wNSIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMTIzLjA1IDEyMy4wNTsiDQoJIHhtbDpzcGFjZT0icHJlc2VydmUiPg0KPGc+DQoJPHBhdGggZD0iTTEyMS4zMjUsMTAuOTI1bC04LjUtOC4zOTljLTIuMy0yLjMtNi4xLTIuMy04LjUsMGwtNDIuNCw0Mi4zOTlMMTguNzI2LDEuNzI2Yy0yLjMwMS0yLjMwMS02LjEwMS0yLjMwMS04LjUsMGwtOC41LDguNQ0KCQljLTIuMzAxLDIuMy0yLjMwMSw2LjEsMCw4LjVsNDMuMSw0My4xbC00Mi4zLDQyLjVjLTIuMywyLjMtMi4zLDYuMSwwLDguNWw4LjUsOC41YzIuMywyLjMsNi4xLDIuMyw4LjUsMGw0Mi4zOTktNDIuNGw0Mi40LDQyLjQNCgkJYzIuMywyLjMsNi4xLDIuMyw4LjUsMGw4LjUtOC41YzIuMy0yLjMsMi4zLTYuMSwwLTguNWwtNDIuNS00Mi40bDQyLjQtNDIuMzk5QzEyMy42MjUsMTcuMTI1LDEyMy42MjUsMTMuMzI1LDEyMS4zMjUsMTAuOTI1eiIvPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPC9zdmc+DQo=);
    background-size: 10px 10px;
    filter: invert();
  }
</style>
